import { useEffect, useRef, useState } from 'react';
import { Camera, CheckCircle2, ImagePlus, Layers, Loader2, PackagePlus, ScanLine, Upload, AlertTriangle, Plus } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { useEscanerTeclado } from '../lib/useEscanerTeclado';
import { pitidoOk, pitidoError } from '../lib/pitido';
import { useCambiosSinGuardar, useCapa, useSubpantalla } from '../ui/navegacionReact';
import Ayuda from '../ui/Ayuda';
import EscanerCamara from '../mobile/EscanerCamara';
import EscanearVarios from './EscanearVarios';
import Propuestas from './Propuestas';
import SelectorUbicacion from '../catalogo-vivo/SelectorUbicacion';
import SelectorCategoria from '../catalogo-vivo/SelectorCategoria';
import BloqueAtributos from '../catalogo-vivo/BloqueAtributos';
import { faltanObligatorios, paraGuardar } from '../catalogo-vivo/fichaAtributos';
import { marcar } from './bandeja';
import {
  UNIDADES_CONTENIDO, avisoDeLaFicha, cambiarCampo, camposVisibles, cuerpoParaGuardar,
  dondeSeEncontro, estadoInicial, marcaDeOrigen, nombreDeFuente, problemaParaGuardar,
  tipoDetectado, usarCoincidencia,
} from './ficha';

/* =====================================================================
   DAR DE ALTA ESCANEANDO · DIRECTRIZ-ALTA-POR-ESCANEO F5 (y F6, F7)
   =====================================================================
   Escanear → la ficha llega armada (catálogo de TEGO y fuentes externas)
   → se corrige lo que haga falta, en el mismo lugar, sin modo aparte →
   «Guardar en inventario». Si el código ya es de un producto de la
   empresa, no hay ficha: «Sumar existencia».

   PRIMERO TELÉFONO: una columna, botones grandes, lo importante arriba.
   MIENTRAS CONSULTA: el código a la vista y el esqueleto de la ficha (el
   servidor tiene un tope de 4 s); nunca una pantalla en blanco.
   NINGÚN «VERIFICADO»: las fuentes son comunitarias o de terceros.
   ===================================================================== */
export default function FichaPrevia({ contexto, irA, Camara = EscanerCamara }) {
  const [lectura, setLectura] = useState(() => (contexto?.codigo
    ? { codigo: String(contexto.codigo), medio: contexto.medio || 'TECLADO' } : null));
  const [camara, setCamara] = useState(false);
  // F7 · «Escanear varios»: la bandeja vive aquí para no perderse al abrir
  // uno de sus productos en la ficha y volver.
  const [varios, setVarios] = useState(false);
  const [bandeja, setBandeja] = useState([]);
  const [deLaBandeja, setDeLaBandeja] = useState(null);
  useCapa(camara, () => setCamara(false));
  // ATRÁS VUELVE UN PASO, no sale de la pantalla: de la ficha a escanear,
  // de un producto de la bandeja a la bandeja, de la bandeja al inicio.
  useSubpantalla('alta-escaneo-ficha', Boolean(lectura), () => setLectura(null));
  useSubpantalla('alta-escaneo-varios', varios, () => { setVarios(false); setBandeja([]); });
  useSubpantalla('alta-escaneo-de-la-bandeja', Boolean(deLaBandeja), () => setDeLaBandeja(null));

  useEscanerTeclado((leido) => { pitidoOk(); setLectura({ codigo: leido, medio: 'LECTOR' }); },
    { activo: !lectura && !camara && !varios });

  if (varios && deLaBandeja) {
    return (
      <Consulta
        key={`bandeja|${deLaBandeja.codigo}`}
        codigo={deLaBandeja.codigo}
        medio="CAMARA"
        irA={irA}
        precargada={deLaBandeja.respuesta}
        cantidadInicial={deLaBandeja.cantidad}
        etiquetaVolver="Volver a la bandeja"
        onHecho={() => setBandeja((b) => marcar(b, deLaBandeja.codigo, 'hecho'))}
        onOtro={() => setDeLaBandeja(null)}
      />
    );
  }
  if (varios) {
    return (
      <EscanearVarios
        bandeja={bandeja}
        setBandeja={setBandeja}
        abrirFicha={setDeLaBandeja}
        onSalir={() => { setVarios(false); setBandeja([]); }}
      />
    );
  }
  if (!lectura) {
    return (
      <PedirCodigo
        camara={camara}
        Camara={Camara}
        onCamara={setCamara}
        onCodigo={(codigo, medio) => setLectura({ codigo, medio })}
        onVarios={() => { setCamara(false); setVarios(true); }}
      />
    );
  }
  return (
    <Consulta
      key={`${lectura.codigo}|${lectura.medio}`}
      codigo={lectura.codigo}
      medio={lectura.medio}
      irA={irA}
      onOtro={() => setLectura(null)}
    />
  );
}

function Encabezado({ subtitulo }) {
  return (
    <div className="space-y-2">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Dar de alta escaneando</h2>
        <p className="text-sm text-gray-500 mt-0.5">{subtitulo}</p>
      </div>
      <Ayuda id="alta_escaneo.pantalla" />
    </div>
  );
}

// ---------------------------------------------------------------------
// 1 · Pedir el código: cámara, lector o teclado
// ---------------------------------------------------------------------
function PedirCodigo({ camara, Camara, onCamara, onCodigo, onVarios }) {
  const [valor, setValor] = useState('');
  return (
    <div className="space-y-4" data-alta-escaneo="pedir">
      <Encabezado subtitulo="Escanea el código de barras del producto. También sirve un lector o escribirlo." />
      <Propuestas />
      {camara ? (
        <Camara
          onCodigo={(t) => { onCamara(false); pitidoOk(); onCodigo(t, 'CAMARA'); }}
          onCerrar={() => onCamara(false)}
        />
      ) : (
        <button
          type="button"
          onClick={() => onCamara(true)}
          className="w-full min-h-14 inline-flex items-center justify-center gap-2 bg-[var(--color-marca)] text-white font-semibold rounded-xl px-4"
        >
          <Camera className="h-5 w-5" aria-hidden="true" /> Escanear con la cámara
        </button>
      )}
      <form
        className="space-y-2"
        onSubmit={(e) => { e.preventDefault(); if (valor.trim()) onCodigo(valor.trim(), 'TECLADO'); }}
      >
        <label htmlFor="ae-codigo" className="block text-sm font-semibold text-gray-700">
          O escribe el código
        </label>
        <div className="flex gap-2">
          <input
            id="ae-codigo"
            className="entrada font-mono flex-1 min-w-0"
            value={valor}
            maxLength={120}
            autoComplete="off"
            inputMode="numeric"
            placeholder="Ej. 9780306406157"
            onChange={(e) => setValor(e.target.value)}
          />
          <button
            type="submit"
            disabled={!valor.trim()}
            className="min-h-11 shrink-0 inline-flex items-center gap-1 bg-white border border-gray-300 text-gray-800 font-semibold rounded-xl px-4 disabled:opacity-60"
          >
            <ScanLine className="h-4 w-4" aria-hidden="true" /> Buscar
          </button>
        </div>
        {!valor.trim() && <p className="text-xs text-gray-500">Escribe los números que vienen bajo las barras.</p>}
      </form>
      <button
        type="button"
        onClick={onVarios}
        className="w-full min-h-12 inline-flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-800 font-semibold rounded-xl px-4"
      >
        <Layers className="h-5 w-5" aria-hidden="true" /> Escanear varios
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------
// 2 · Consultar y decidir qué se enseña
// ---------------------------------------------------------------------
function Consulta({ codigo, medio, irA, onOtro, precargada = null, cantidadInicial = 1, onHecho,
  etiquetaVolver = 'Escanear otro' }) {
  const [respuesta, setRespuesta] = useState(precargada);
  const [error, setError] = useState(null);
  const [esOtro, setEsOtro] = useState(false);

  useEffect(() => {
    if (precargada) return undefined;
    let vivo = true;
    api.get(`/alta-escaneo/ficha?codigo=${encodeURIComponent(codigo)}`)
      .then((r) => { if (vivo) setRespuesta(r); })
      .catch((e) => {
        if (!vivo) return;
        pitidoError();
        setError(e instanceof ErrorApi ? e.message
          : 'No hay conexión con el servidor. Revisa la red y vuelve a escanear.');
      });
    return () => { vivo = false; };
  }, [codigo, precargada]);

  if (error) {
    return (
      <Aviso tono="problema" texto={error}>
        <BotonSecundario onClick={onOtro} icono={ScanLine}>Escanear otro</BotonSecundario>
      </Aviso>
    );
  }
  if (!respuesta) return <Esqueleto codigo={codigo} />;

  const l = respuesta.lectura;
  if (l.mensaje && !l.consultar_afuera && (l.tipo === 'QR_TEGO' || !l.digito_valido)) {
    return (
      <Aviso tono="problema" texto={l.mensaje} data="relectura">
        <BotonPrincipal onClick={onOtro} icono={ScanLine}>Volver a escanear</BotonPrincipal>
        {l.tipo === 'QR_TEGO' && irA && (
          <BotonSecundario onClick={() => irA('escanear', { codigo, medio })} icono={PackagePlus}>
            Abrir en Escanear
          </BotonSecundario>
        )}
      </Aviso>
    );
  }
  if (respuesta.ya_es_de && !esOtro) {
    return (
      <SumarExistencia
        ya={respuesta.ya_es_de}
        codigo={codigo}
        medio={medio}
        onOtro={onOtro}
        onEsOtroProducto={() => setEsOtro(true)}
      />
    );
  }
  return (
    <Ficha respuesta={respuesta} medio={medio} esOtroProducto={esOtro} onOtro={onOtro}
      cantidadInicial={cantidadInicial} onHecho={onHecho} etiquetaVolver={etiquetaVolver} />
  );
}

function Esqueleto({ codigo }) {
  return (
    <div className="space-y-4" data-alta-escaneo="consultando" aria-busy="true">
      <Encabezado subtitulo="Buscando el producto en el catálogo de TEGO y en las fuentes." />
      <div className="flex items-center gap-2 text-sm text-gray-700">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Buscando <span className="font-mono">{codigo}</span>…
      </div>
      <div className="h-48 rounded-xl bg-gray-100 animate-pulse" />
      <div className="h-6 w-3/4 rounded bg-gray-100 animate-pulse" />
      <div className="h-10 rounded bg-gray-100 animate-pulse" />
      <div className="h-10 rounded bg-gray-100 animate-pulse" />
    </div>
  );
}

// ---------------------------------------------------------------------
// 3 · La Ficha previa
// ---------------------------------------------------------------------
function Ficha({ respuesta, medio, esOtroProducto, onOtro, cantidadInicial = 1, onHecho,
  etiquetaVolver = 'Escanear otro' }) {
  const [estado, setEstado] = useState(() => estadoInicial(respuesta));
  const [empresa, setEmpresa] = useState({ sku: '', precio_venta: '', costo: '', cantidad: String(cantidadInicial),
    ubicacion: '', unidad_medida: 'PIEZA' });
  // CATÁLOGO VIVO F3 · la ubicación se elige en el árbol (SelectorUbicacion):
  // `empresa.ubicacion` sigue siendo el código que se manda a guardar.
  const [ubicacionElegida, setUbicacionElegida] = useState(null);
  // CATÁLOGO VIVO F2 y F5 · la categoría llega PROPUESTA (ISBN → Libro, bolsa de
  // Digi-Key → Componente electrónico, lo que la empresa enseñó) y la ficha pide
  // los datos de su plantilla. Nada se guarda sin que la persona toque «Guardar».
  const [idCategoria, setIdCategoria] = useState(null);
  const [propuesta, setPropuesta] = useState(null);
  const [memoriaAtributos, setMemoriaAtributos] = useState({});
  const [origenesAtributos, setOrigenesAtributos] = useState({});
  const [plantillaAtributos, setPlantillaAtributos] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [guardado, setGuardado] = useState(null);

  // Lo corregido y lo de la empresa es trabajo: salir sin guardar lo pregunta.
  useCambiosSinGuardar(() => !guardado && (
    JSON.stringify(estado.campos) !== JSON.stringify(estado.valoresFuente)
    || Boolean(empresa.precio_venta || empresa.costo || empresa.sku)));

  const l = estado.lectura;
  const fuenteCategoria = estado.origen?.categoria_sugerida || estado.origen?.nombre || null;
  useEffect(() => {
    let vigente = true;
    api.post('/catalogo-vivo/clasificar/proponer', {
      tipo_codigo: l?.tipo || null, lectura: l?.lectura || null,
      fuente: fuenteCategoria && fuenteCategoria !== 'usuario' ? fuenteCategoria : null,
      categoria_externa: estado.campos?.categoria_sugerida || null,
    }).then((r) => {
      if (!vigente || !r) return;
      setPropuesta(r);
      if (r.propuesta?.id) setIdCategoria((x) => x ?? r.propuesta.id);
      if (r.unidad_sugerida) setEmpresa((x) => ({ ...x, unidad_medida: r.unidad_sugerida }));
      if (r.cantidad) setEmpresa((x) => (x.cantidad === String(cantidadInicial) ? { ...x, cantidad: String(r.cantidad) } : x));
      const deLaEtiqueta = Object.fromEntries(Object.entries(r.datos || {})
        .filter(([k]) => ['numero_parte', 'fabricante', 'lote'].includes(k)));
      if (Object.keys(deLaEtiqueta).length) {
        setMemoriaAtributos((m) => ({ ...deLaEtiqueta, ...m }));
        setOrigenesAtributos(Object.fromEntries(Object.keys(deLaEtiqueta).map((k) => [k, 'de la etiqueta'])));
      }
    }).catch(() => {});
    return () => { vigente = false; };
    // Solo al abrir la ficha: lo que se propone sale de lo que se leyó.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const faltanAtributos = idCategoria ? faltanObligatorios(plantillaAtributos, memoriaAtributos) : [];
  const aviso = avisoDeLaFicha(estado);
  const problema = problemaParaGuardar(estado, empresa)
    || (faltanAtributos.length ? `Falta ${faltanAtributos.map((n) => `«${n}»`).join(' y ')} para guardar.` : null);
  const visibles = camposVisibles(l, estado.campos);

  // Lo de la categoría va después de crear el producto, que es quien le da id.
  // Si esto falla, el producto ya existe: se dice dónde completarlo.
  async function guardarCategoria(idProducto) {
    if (!idProducto || !idCategoria) return;
    const valores = paraGuardar(plantillaAtributos, memoriaAtributos);
    const fuente = propuesta?.propuesta && fuenteCategoria !== 'usuario' ? (fuenteCategoria || 'codigo') : null;
    try {
      if (Object.keys(valores).length) {
        await api.put(`/catalogo-vivo/catalogo/productos/${idProducto}/atributos`, { valores });
      }
      if (fuente && propuesta.propuesta.id !== idCategoria) {
        await api.post('/catalogo-vivo/clasificar/correccion', {
          fuente, categoria_externa: estado.campos?.categoria_sugerida || null,
          id_categoria_propuesta: propuesta.propuesta.id, id_categoria_elegida: idCategoria, id_producto: idProducto });
      }
    } catch {
      setError('El producto quedó guardado. Los datos de su categoría no se guardaron: complétalos en Administración → Catálogo → Fichas incompletas.');
    }
  }

  async function guardar() {
    if (problema) return;
    setGuardando(true);
    setError(null);
    try {
      const r = await api.post('/alta-escaneo/guardar',
        { ...cuerpoParaGuardar(estado, empresa, { medio, esOtroProducto }), id_categoria: idCategoria });
      await guardarCategoria(r?.id_producto);
      pitidoOk();
      setGuardado(r);
      onHecho?.(r);
    } catch (e) {
      pitidoError();
      setError(e instanceof ErrorApi ? e.message
        : 'No hay conexión con el servidor. La ficha sigue aquí: vuelve a tocar «Guardar en inventario».');
    } finally {
      setGuardando(false);
    }
  }

  if (guardado) {
    return <Guardado datos={guardado} nombre={estado.campos.nombre} onOtro={onOtro} etiqueta={etiquetaVolver} />;
  }

  return (
    <div className="space-y-4 pb-24" data-alta-escaneo="ficha">
      <Encabezado subtitulo="Revisa la ficha. Puedes cambiar cualquier dato y la imagen antes de guardar." />
      {aviso && <Aviso tono={aviso.tono} texto={aviso.texto} data="aviso" />}
      {esOtroProducto && (
        <Aviso tono="atencion" data="otro"
          texto="Se creará otro producto con este código. Al escanearlo se seguirá abriendo el primero." />
      )}

      <Imagen estado={estado} setEstado={setEstado} codigo={l?.lectura} />

      <div>
        <p className="text-2xl font-semibold text-gray-900 break-words" data-alta-escaneo="nombre">
          {estado.campos.nombre || 'Producto sin nombre'}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          <span className="font-mono">{l?.lectura}</span> · {tipoDetectado(l)}
          {dondeSeEncontro(estado) && <> · {dondeSeEncontro(estado)}</>}
        </p>
      </div>

      {estado.otras.length > 0 && (
        <div className="rounded-xl border border-gray-200 p-3 space-y-2" data-alta-escaneo="otras">
          <p className="text-sm font-semibold text-gray-700">Otras coincidencias</p>
          {estado.otras.map((o) => (
            <div key={`${o.fuente}-${o.nombre}`} className="flex items-center justify-between gap-2">
              <span className="text-sm text-gray-800 break-words">
                {o.nombre} <span className="text-xs text-gray-500">({nombreDeFuente(o.fuente)})</span>
              </span>
              <button type="button" onClick={() => setEstado((e) => usarCoincidencia(e, o))}
                className="min-h-11 shrink-0 text-sm font-semibold text-[var(--color-marca)] px-2">
                Usar este nombre
              </button>
            </div>
          ))}
        </div>
      )}

      <fieldset className="space-y-3" data-alta-escaneo="identidad">
        <legend className="text-sm font-semibold text-gray-700">Datos del producto</legend>
        <Ayuda id="alta_escaneo.origen" />
        {visibles.map((c) => (
          <Campo key={c.campo} def={c} valor={estado.campos[c.campo]} origen={estado.origen[c.campo]}
            onCambio={(v) => setEstado((e) => cambiarCampo(e, c.campo, v))} />
        ))}
      </fieldset>

      <div className="space-y-3" data-alta-escaneo="categoria">
        <SelectorCategoria id="ae-categoria" valor={idCategoria} onElegir={(id) => setIdCategoria(id)} />
        {propuesta?.propuesta && idCategoria === propuesta.propuesta.id && (
          <p className="text-xs text-gray-600" data-alta-escaneo="categoria-sugerida">Sugerida: {propuesta.motivo}</p>
        )}
        <BloqueAtributos idCategoria={idCategoria} memoria={memoriaAtributos} onCambio={setMemoriaAtributos}
          origenes={origenesAtributos} onPlantilla={setPlantillaAtributos} sinIdentidad />
      </div>

      <fieldset className="space-y-3" data-alta-escaneo="empresa">
        <legend className="text-sm font-semibold text-gray-700">Datos de tu empresa</legend>
        <Ayuda id="alta_escaneo.empresa" />
        <CampoEmpresa id="ae-cantidad" etiqueta="Cantidad que entra ahora" tipo="number" valor={empresa.cantidad}
          onCambio={(v) => setEmpresa((x) => ({ ...x, cantidad: v }))} />
        <SelectorUbicacion id="ae-ubicacion" valor={ubicacionElegida}
          onElegir={(u) => { setUbicacionElegida(u); setEmpresa((x) => ({ ...x, ubicacion: u?.codigo || '' })); }} />
        <CampoEmpresa id="ae-precio" etiqueta="Precio de venta" tipo="number" valor={empresa.precio_venta}
          onCambio={(v) => setEmpresa((x) => ({ ...x, precio_venta: v }))} />
        <CampoEmpresa id="ae-costo" etiqueta="Costo" tipo="number" valor={empresa.costo}
          onCambio={(v) => setEmpresa((x) => ({ ...x, costo: v }))} />
        <CampoEmpresa id="ae-sku" etiqueta="SKU (si lo dejas vacío, el mismo código)" valor={empresa.sku}
          onCambio={(v) => setEmpresa((x) => ({ ...x, sku: v.toUpperCase() }))} />
      </fieldset>

      {estado.atribuciones.length > 0 && (
        <p className="text-[11px] text-gray-400" data-alta-escaneo="atribucion">{estado.atribuciones.join(' · ')}</p>
      )}
      <Ayuda id="alta_escaneo.ficha" />

      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      <div className="sticky bottom-0 bg-white/95 pt-2 pb-3 space-y-2 border-t border-gray-100">
        <BotonPrincipal onClick={guardar} icono={guardando ? Loader2 : PackagePlus}
          disabled={Boolean(problema) || guardando}>
          {guardando ? 'Guardando…' : 'Guardar en inventario'}
        </BotonPrincipal>
        {problema && <p className="text-xs text-gray-600" data-alta-escaneo="falta">{problema}</p>}
        <BotonSecundario onClick={onOtro} icono={ScanLine}>
          {etiquetaVolver === 'Escanear otro' ? 'Descartar' : etiquetaVolver}
        </BotonSecundario>
      </div>
    </div>
  );
}

function Campo({ def, valor, origen, onCambio }) {
  const id = `ae-${def.campo}`;
  const marca = marcaDeOrigen(origen);
  const comun = { id, className: 'entrada', onChange: (e) => onCambio(e.target.value) };
  let control;
  if (def.tipo === 'largo') {
    control = <textarea {...comun} rows={3} maxLength={def.max} value={valor ?? ''} />;
  } else if (def.tipo === 'unidad') {
    control = (
      <select {...comun} value={valor ?? ''}>
        <option value="">Sin unidad</option>
        {UNIDADES_CONTENIDO.map((u) => <option key={u} value={u}>{u}</option>)}
      </select>
    );
  } else if (def.tipo === 'lista') {
    control = <input {...comun} value={Array.isArray(valor) ? valor.join(', ') : (valor ?? '')} />;
  } else {
    control = (
      <input {...comun} value={valor ?? ''} maxLength={def.max}
        inputMode={def.tipo === 'texto' ? 'text' : 'decimal'} />
    );
  }
  return (
    <div data-campo={def.campo}>
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="block text-sm font-semibold text-gray-700">{def.etiqueta}</label>
        {marca && <span className="text-[11px] text-gray-500" data-marca={def.campo}>{marca}</span>}
      </div>
      {control}
    </div>
  );
}

function CampoEmpresa({ id, etiqueta, tipo = 'text', valor, onCambio }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-gray-700">{etiqueta}</label>
      <input id={id} className="entrada" type={tipo} inputMode={tipo === 'number' ? 'decimal' : 'text'}
        min={tipo === 'number' ? 0 : undefined} value={valor} onChange={(e) => onCambio(e.target.value)} />
    </div>
  );
}

// ---------------------------------------------------------------------
// La imagen: la guardada en TEGO o un hueco con «Tomar foto» y «Subir imagen»
// ---------------------------------------------------------------------
function Imagen({ estado, setEstado, codigo }) {
  const [url, setUrl] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState(null);
  const foto = useRef(null);
  const archivo = useRef(null);

  useEffect(() => {
    let vivo = true;
    let creada = null;
    setUrl(null);
    if (estado.imagen?.id) {
      api.bajar(`/alta-escaneo/imagen/${estado.imagen.id}`)
        .then((b) => {
          if (!vivo || typeof URL === 'undefined' || !URL.createObjectURL) return;
          creada = URL.createObjectURL(b);
          setUrl(creada);
        })
        .catch(() => {});
    }
    return () => { vivo = false; if (creada) URL.revokeObjectURL(creada); };
  }, [estado.imagen?.id]);

  async function subir(e) {
    const a = e.target.files?.[0];
    e.target.value = '';
    if (!a) return;
    setSubiendo(true);
    setError(null);
    try {
      const cuerpo = new FormData();
      cuerpo.append('archivo', a);
      cuerpo.append('codigo', codigo || '');
      const r = await api.enviarFormulario('/alta-escaneo/imagen', cuerpo);
      setEstado((x) => ({ ...x, imagen: { id: r.id, fuente: 'usuario' }, imagenNoSeTrajo: false }));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No hay conexión con el servidor. Vuelve a intentarlo.');
    } finally {
      setSubiendo(false);
    }
  }

  return (
    <div className="space-y-2" data-alta-escaneo="imagen">
      {estado.imagen?.id ? (
        <div className="rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-center h-56 overflow-hidden">
          {url ? <img src={url} alt={estado.campos.nombre || 'Imagen del producto'} className="max-h-56 object-contain" />
            : <Loader2 className="h-6 w-6 animate-spin text-gray-400" aria-hidden="true" />}
        </div>
      ) : (
        <div className="rounded-xl border-2 border-dashed border-gray-300 h-40 flex flex-col items-center justify-center gap-1 text-gray-500 text-sm px-4 text-center" data-alta-escaneo="sin-imagen">
          <ImagePlus className="h-6 w-6" aria-hidden="true" />
          {estado.imagenNoSeTrajo ? 'La imagen de la fuente no llegó a tiempo. Toma una foto o sube una.'
            : 'Sin imagen. Toma una foto o sube una.'}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => foto.current?.click()} disabled={subiendo}
          className="min-h-12 inline-flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-800 font-semibold rounded-xl px-3">
          <Camera className="h-5 w-5" aria-hidden="true" /> Tomar foto
        </button>
        <button type="button" onClick={() => archivo.current?.click()} disabled={subiendo}
          className="min-h-12 inline-flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-800 font-semibold rounded-xl px-3">
          <Upload className="h-5 w-5" aria-hidden="true" /> Subir imagen
        </button>
      </div>
      <input ref={foto} type="file" accept="image/*" capture="environment" className="hidden" onChange={subir} />
      <input ref={archivo} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={subir} />
      <Ayuda id="alta_escaneo.imagen" />
      {subiendo && <p className="text-xs text-gray-600">Subiendo la imagen…</p>}
      {error && <p role="alert" className="text-xs text-red-700">{error}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------
// «Sumar existencia»: el código ya es de un producto de la empresa
// ---------------------------------------------------------------------
function SumarExistencia({ ya, codigo, medio, onOtro, onEsOtroProducto }) {
  const [cantidad, setCantidad] = useState('1');
  // CATÁLOGO VIVO F3 · elegida en el árbol; `ubicacion` es su código.
  const [elegida, setElegida] = useState(null);
  const ubicacion = elegida?.codigo || '';
  const [hecho, setHecho] = useState(null);
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const { tieneNivel } = useAuth() || {};

  const falta = !(Number(cantidad) > 0) ? 'Escribe cuántas piezas entran.'
    : (!ubicacion ? 'Elige en «Ubicación» dónde entra la mercancía.' : null);

  async function sumar() {
    setEnviando(true);
    setError(null);
    try {
      const r = await api.post('/alta-escaneo/sumar', { codigo, cantidad: String(cantidad), ubicacion,
        medio: ['CAMARA', 'LECTOR', 'TECLADO'].includes(medio) ? medio : 'TECLADO' });
      pitidoOk();
      setHecho(r);
    } catch (e) {
      pitidoError();
      setError(e instanceof ErrorApi ? e.message : 'No hay conexión con el servidor. Vuelve a intentarlo.');
    } finally {
      setEnviando(false);
    }
  }

  if (hecho) {
    return (
      <Aviso tono="bien" data="sumado"
        texto={hecho.movimiento?.estado === 'PENDIENTE_APROBACION' ? hecho.movimiento.mensaje
          : `Listo: entraron ${cantidad} a «${hecho.nombre}» (${hecho.sku}).`}>
        <BotonPrincipal onClick={onOtro} icono={ScanLine}>Escanear otro</BotonPrincipal>
      </Aviso>
    );
  }
  return (
    <div className="space-y-4" data-alta-escaneo="sumar">
      <Encabezado subtitulo="Este código ya es de un producto de tu empresa." />
      <div className="rounded-xl border border-gray-200 p-3">
        <p className="text-lg font-semibold text-gray-900 break-words">{ya.nombre}</p>
        <p className="text-xs text-gray-500"><span className="font-mono">{ya.sku}</span> · {codigo}</p>
      </div>
      <CampoEmpresa id="ae-sumar-cantidad" etiqueta="Cantidad que entra" tipo="number" valor={cantidad} onCambio={setCantidad} />
      <SelectorUbicacion id="ae-sumar-ubicacion" valor={elegida} onElegir={setElegida} />
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      <BotonPrincipal onClick={sumar} icono={enviando ? Loader2 : Plus} disabled={Boolean(falta) || enviando}>
        Sumar existencia
      </BotonPrincipal>
      {falta && <p className="text-xs text-gray-600">{falta}</p>}
      {tieneNivel?.('ENCARGADO') !== false && (
        <BotonSecundario onClick={onEsOtroProducto} icono={PackagePlus}>Es otro producto</BotonSecundario>
      )}
      <Ayuda id="alta_escaneo.otro_producto" />
      <BotonSecundario onClick={onOtro} icono={ScanLine}>Escanear otro</BotonSecundario>
    </div>
  );
}

function Guardado({ datos, nombre, onOtro, etiqueta = 'Escanear otro' }) {
  const pendiente = datos.movimiento?.estado === 'PENDIENTE_APROBACION';
  return (
    <div className="space-y-4" data-alta-escaneo="guardado">
      <div className="flex items-start gap-2 text-green-800">
        <CheckCircle2 className="h-6 w-6 shrink-0" aria-hidden="true" />
        <div>
          <p className="text-lg font-semibold">Guardado en inventario</p>
          <p className="text-sm">«{nombre}» quedó con el SKU <span className="font-mono">{datos.sku}</span>
            {datos.movimiento && !pendiente ? ` y entraron ${datos.movimiento.cantidad}.` : '.'}</p>
          {pendiente && <p className="text-sm text-amber-800 mt-1">{datos.movimiento.mensaje}</p>}
          {datos.catalogo === 'aportado' && (
            <p className="text-xs text-gray-600 mt-1">La próxima vez que alguien lo escanee, TEGO ya lo conocerá.</p>
          )}
        </div>
      </div>
      <BotonPrincipal onClick={onOtro} icono={ScanLine}>{etiqueta}</BotonPrincipal>
    </div>
  );
}

// ---------------------------------------------------------------------
// Piezas
// ---------------------------------------------------------------------
const TONOS = {
  bien: 'border-green-200 bg-green-50 text-green-900',
  atencion: 'border-amber-200 bg-amber-50 text-amber-900',
  problema: 'border-red-200 bg-red-50 text-red-900',
};

function Aviso({ tono, texto, data, children }) {
  return (
    <div className="space-y-3" data-alta-escaneo={data}>
      <div className={`flex gap-2 rounded-xl border p-3 text-sm ${TONOS[tono] || TONOS.atencion}`} role="status">
        {tono === 'bien' ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
          : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />}
        <span>{texto}</span>
      </div>
      {children}
    </div>
  );
}

function BotonPrincipal({ onClick, icono: Icono, disabled, children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className="w-full min-h-12 inline-flex items-center justify-center gap-2 bg-[var(--color-marca)] disabled:opacity-60 text-white font-semibold rounded-xl px-4">
      {Icono && <Icono className={`h-5 w-5 ${Icono === Loader2 ? 'animate-spin' : ''}`} aria-hidden="true" />}
      {children}
    </button>
  );
}

function BotonSecundario({ onClick, icono: Icono, children }) {
  return (
    <button type="button" onClick={onClick}
      className="w-full min-h-11 inline-flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-800 font-semibold rounded-xl px-4">
      {Icono && <Icono className="h-5 w-5" aria-hidden="true" />}
      {children}
    </button>
  );
}
